import React, { useCallback, useEffect, useState } from 'react';
import { Package, Loader2, RotateCcw, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import {
  recordContainerReturn,
  adjustContainerBalance,
  assignContainersToAddress,
  quoteContainerRefund,
  DepositRefundQuote,
} from '../lib/customerOrders';
import { RefundDestination, describeRefundOutcome } from '../lib/refunds';
import RefundDestinationPicker from './refunds/RefundDestinationPicker';

interface BalanceRow {
  stock_item_id: string;
  quantity: number;
  deposit_cents_held: number;
  item_name: string;
  customer_address_id: string | null;
  address_label: string;
}

interface SavedAddress {
  id: string;
  label: string;
}

interface LedgerRow {
  id: string;
  stock_item_id: string;
  type: 'delivered' | 'returned' | 'lost' | 'adjustment';
  quantity: number;
  deposit_cents_per_unit: number;
  refund_method: string | null;
  notes: string | null;
  /** For returned/lost rows: FIFO lot breakdown proving the rates used. */
  allocation: Array<{ quantity: number; rate_cents: number; refund_cents: number }> | null;
  created_at: string;
  created_by: string | null;
  item_name: string;
  staff_name: string | null;
}

interface DepositItem {
  id: string;
  name: string;
  deposit_cents: number;
}

const fmtMoney = (cents: number) => `$${(Math.abs(cents) / 100).toFixed(2)}`;

const TYPE_LABELS: Record<LedgerRow['type'], string> = {
  delivered: 'Delivered',
  returned: 'Returned',
  lost: 'Lost',
  adjustment: 'Adjusted',
};

/** Signed on-hand delta a ledger row represents. */
const rowDelta = (t: Pick<LedgerRow, 'type' | 'quantity'>) =>
  t.type === 'delivered' ? t.quantity : t.type === 'adjustment' ? t.quantity : -t.quantity;

/**
 * Container deposits card: how many returnable containers (e.g. water jugs)
 * this customer has on hand, the deposits held, a full audit trail, manual
 * balance adjustments, and a "record pickup" action for refunds/losses.
 * Renders nothing for customers with no container activity (unless the
 * business sells deposit-bearing items, in which case a slim "track
 * containers" entry point shows so staff can seed a starting balance).
 */
export function CustomerContainersCard({
  businessId,
  customerId,
  onWalletChanged,
}: {
  businessId: string;
  customerId: string;
  onWalletChanged?: () => void;
}) {
  const [balances, setBalances] = useState<BalanceRow[]>([]);
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [depositItems, setDepositItems] = useState<DepositItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  // Pickup / refund form
  const [showPickup, setShowPickup] = useState(false);
  const [pickupItem, setPickupItem] = useState('');
  /** '' = containers not tied to an address. A uuid spends that site, then unassigned. */
  const [pickupAddressId, setPickupAddressId] = useState('');
  const [pickupReturned, setPickupReturned] = useState('');
  const [pickupLost, setPickupLost] = useState('');
  const [pickupRefund, setPickupRefund] = useState<RefundDestination>('wallet');
  const [pickupReference, setPickupReference] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');
  const [pickupBusy, setPickupBusy] = useState(false);
  const [pickupError, setPickupError] = useState<string | null>(null);
  const [pickupSuccess, setPickupSuccess] = useState<string | null>(null);
  const [pickupQuote, setPickupQuote] = useState<DepositRefundQuote | null>(null);
  // Manual adjustment form
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustItem, setAdjustItem] = useState('');
  const [adjustDir, setAdjustDir] = useState<'add' | 'remove'>('add');
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustTrackDeposit, setAdjustTrackDeposit] = useState(false);
  const [adjustBusy, setAdjustBusy] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const [adjustSuccess, setAdjustSuccess] = useState<string | null>(null);
  const [assignItem, setAssignItem] = useState('');
  const [assignAddressId, setAssignAddressId] = useState('');
  const [assignQty, setAssignQty] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [{ data: txs, error }, { data: items }] = await Promise.all([
        supabase
          .from('container_transactions')
          .select('id, stock_item_id, type, quantity, deposit_cents_per_unit, refund_method, notes, allocation, created_at, created_by')
          .eq('customer_id', customerId)
          .order('created_at', { ascending: false })
          .limit(200),
        supabase
          .from('stock_items')
          .select('id, name, deposit_cents')
          .eq('business_id', businessId)
          .gt('deposit_cents', 0)
          .eq('is_deleted', false)
          .order('name'),
      ]);
      if (error) throw error;
      setDepositItems((items || []) as DepositItem[]);

      const rows = txs || [];
      if (rows.length === 0) {
        setBalances([]);
        setLedger([]);
        return;
      }

      // Names for items in the ledger that may no longer carry a deposit
      const itemIds = Array.from(new Set(rows.map(t => t.stock_item_id)));
      const { data: ledgerItems } = await supabase
        .from('stock_items')
        .select('id, name')
        .in('id', itemIds);
      const names = new Map((ledgerItems || []).map(i => [i.id, i.name]));

      // Staff names for the audit trail (users RLS is self-only, so resolve
      // through the auth-checked RPC).
      const staffIds = Array.from(new Set(rows.map(t => t.created_by).filter(Boolean))) as string[];
      let staffNames = new Map<string, string>();
      if (staffIds.length > 0) {
        const { data: staff } = await supabase.rpc('get_staff_display_names', {
          p_business_id: businessId,
          p_user_ids: staffIds,
        });
        staffNames = new Map(
          ((staff || []) as Array<{ id: string; display_name: string }>).map(u => [u.id, u.display_name])
        );
      }

      setLedger(
        rows.map(t => ({
          ...t,
          item_name: names.get(t.stock_item_id) || 'Item',
          staff_name: t.created_by ? staffNames.get(t.created_by) || null : null,
        })) as LedgerRow[]
      );

      const { data: addrRows } = await supabase
        .from('customer_addresses')
        .select('id, label, line1')
        .eq('customer_id', customerId)
        .order('is_default_shipping', { ascending: false });
      const saved: SavedAddress[] = (addrRows || []).map(a => ({
        id: a.id,
        label: (a.label && a.label.trim()) || a.line1 || 'Delivery address',
      }));
      setAddresses(saved);
      const addressName = new Map(saved.map(a => [a.id, a.label]));

      const { data: siteRows, error: siteError } = await supabase
        .from('customer_container_balances_by_address')
        .select('stock_item_id, customer_address_id, quantity, deposit_cents_held')
        .eq('customer_id', customerId);

      // Prefer the per-address view. Fall back to the account ledger total
      // (last 200 movements) when that view isn't available.
      let rowsOut: BalanceRow[];
      if (!siteError && siteRows) {
        rowsOut = siteRows
          .filter(r => Number(r.quantity) !== 0 || Number(r.deposit_cents_held) !== 0)
          .map(r => ({
            stock_item_id: r.stock_item_id,
            quantity: Number(r.quantity) || 0,
            deposit_cents_held: Number(r.deposit_cents_held) || 0,
            item_name: names.get(r.stock_item_id) || 'Item',
            customer_address_id: r.customer_address_id,
            address_label: r.customer_address_id
              ? (addressName.get(r.customer_address_id) || 'Delivery address')
              : 'Not tied to an address',
          }));
      } else {
        const byItem = new Map<string, BalanceRow>();
        for (const t of rows) {
          const delta = rowDelta(t);
          const cur = byItem.get(t.stock_item_id) || {
            stock_item_id: t.stock_item_id,
            quantity: 0,
            deposit_cents_held: 0,
            item_name: names.get(t.stock_item_id) || 'Item',
            customer_address_id: null,
            address_label: 'Account',
          };
          cur.quantity += delta;
          cur.deposit_cents_held += delta * t.deposit_cents_per_unit;
          byItem.set(t.stock_item_id, cur);
        }
        rowsOut = Array.from(byItem.values());
      }

      const missingNames = rowsOut.map(r => r.stock_item_id).filter(id => !names.has(id));
      if (missingNames.length > 0) {
        const { data: extra } = await supabase.from('stock_items').select('id, name').in('id', missingNames);
        for (const item of extra || []) names.set(item.id, item.name);
        for (const row of rowsOut) row.item_name = names.get(row.stock_item_id) || row.item_name;
      }

      // Deposits held: exact lot value (rates actually paid).
      await Promise.all(
        rowsOut
          .filter(b => b.quantity > 0)
          .map(async b => {
            try {
              const q = await quoteContainerRefund({
                businessId,
                customerId,
                stockItemId: b.stock_item_id,
                quantity: 0,
                addressId: b.customer_address_id,
                unassignedOnly: !siteError && b.customer_address_id == null,
              });
              b.deposit_cents_held = q.held_value_cents;
            } catch { /* fall back to the ledger estimate */ }
          })
      );
      setBalances(rowsOut);
    } catch (err) {
      console.error('Failed to load container balances:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId, businessId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Live refund quote: exactly what returning N containers pays out, lot by lot.
  useEffect(() => {
    const returned = Math.max(0, parseInt(pickupReturned) || 0);
    if (!showPickup || !pickupItem || returned === 0) {
      setPickupQuote(null);
      return;
    }
    let stale = false;
    const t = setTimeout(async () => {
      try {
        const q = await quoteContainerRefund({
          businessId,
          customerId,
          stockItemId: pickupItem,
          quantity: returned,
          addressId: pickupAddressId || null,
          unassignedOnly: pickupAddressId === '',
        });
        if (!stale) setPickupQuote(q);
      } catch {
        if (!stale) setPickupQuote(null);
      }
    }, 250);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [showPickup, pickupItem, pickupReturned, pickupAddressId, businessId, customerId]);

  const heldItems = balances.filter(b => b.quantity > 0);
  const totalHeldCents = balances.reduce((n, b) => n + Math.max(0, b.deposit_cents_held), 0);
  const hasActivity = ledger.length > 0;

  if (loading) return null;
  // Nothing to track and nothing trackable — stay out of the way entirely.
  if (!hasActivity && depositItems.length === 0) return null;

  const openAdjust = () => {
    setShowAdjust(v => !v);
    setShowPickup(false);
    setAdjustItem(heldItems[0]?.stock_item_id || depositItems[0]?.id || '');
    setAdjustError(null);
    setAdjustSuccess(null);
  };

  // No history yet: slim entry point so staff can seed a starting balance
  // (e.g. jugs the customer had before container tracking existed).
  if (!hasActivity && !showAdjust) {
    return (
      <button
        onClick={openAdjust}
        className="w-full text-left bg-white rounded-[12px] border border-dashed border-gray-300 px-5 py-3.5 flex items-center gap-2.5 text-sm font-medium text-gray-500 hover:border-blue-300 hover:text-blue-600 transition-colors"
      >
        <Package className="h-4 w-4" />
        Track containers for this customer (record a starting balance)
      </button>
    );
  }

  const handlePickup = async () => {
    const returned = Math.max(0, parseInt(pickupReturned) || 0);
    const lost = Math.max(0, parseInt(pickupLost) || 0);
    if (!pickupItem || returned + lost === 0) return;
    setPickupBusy(true);
    setPickupError(null);
    setPickupSuccess(null);
    try {
      const res = await recordContainerReturn({
        businessId,
        customerId,
        stockItemId: pickupItem,
        returned,
        lost,
        refundMethod: pickupRefund,
        notes: [pickupNotes.trim(), pickupReference.trim() ? `Ref: ${pickupReference.trim()}` : ''].filter(Boolean).join(' — ') || undefined,
        addressId: pickupAddressId || null,
        unassignedOnly: pickupAddressId === '',
      });
      if (res.refund_cents <= 0) {
        setPickupSuccess('Recorded.');
      } else {
        setPickupSuccess(
          `Recorded — ${describeRefundOutcome({
            cardCents: res.card_refunded_cents ?? 0,
            walletCents: res.wallet_credited_cents ?? 0,
            cashCents: res.cash_refunded_cents ?? 0,
            destination: pickupRefund,
          })}.`
        );
      }
      setPickupReturned('');
      setPickupLost('');
      setPickupNotes('');
      setPickupReference('');
      await load();
      if (res.refund_cents > 0 && (res.wallet_credited_cents ?? 0) > 0) {
        onWalletChanged?.();
      }
    } catch (err: any) {
      setPickupError(err?.message || 'Failed to record pickup');
    } finally {
      setPickupBusy(false);
    }
  };

  const handleAdjust = async () => {
    const qty = Math.max(0, parseInt(adjustQty) || 0);
    const reason = adjustReason.trim();
    if (!adjustItem || qty === 0 || !reason) return;
    const delta = adjustDir === 'add' ? qty : -qty;
    const itemDeposit = depositItems.find(i => i.id === adjustItem)?.deposit_cents ?? 0;
    setAdjustBusy(true);
    setAdjustError(null);
    setAdjustSuccess(null);
    try {
      const res = await adjustContainerBalance({
        businessId,
        customerId,
        stockItemId: adjustItem,
        delta,
        notes: reason,
        depositCentsPerUnit: adjustTrackDeposit ? itemDeposit : 0,
      });
      setAdjustSuccess(`Balance updated — ${res.balance} × ${res.item_name} on hand.`);
      setAdjustQty('');
      setAdjustReason('');
      await load();
    } catch (err: any) {
      setAdjustError(err?.message || 'Failed to adjust balance');
    } finally {
      setAdjustBusy(false);
    }
  };

  // Items selectable in the adjust form: every deposit-bearing product, plus
  // any item with ledger history (so legacy items stay adjustable).
  const adjustableItems: Array<{ id: string; name: string }> = (() => {
    const map = new Map<string, string>();
    for (const i of depositItems) map.set(i.id, i.name);
    for (const b of balances) if (!map.has(b.stock_item_id)) map.set(b.stock_item_id, b.item_name);
    return Array.from(map, ([id, name]) => ({ id, name }));
  })();

  const onHandFor = (stockItemId: string) =>
    balances.filter(b => b.stock_item_id === stockItemId).reduce((n, b) => n + b.quantity, 0);

  const unassignedHeld = heldItems.filter(b => b.customer_address_id == null && b.address_label !== 'Account');

  const handleAssign = async () => {
    const qty = Math.max(0, parseInt(assignQty) || 0);
    if (!assignItem || !assignAddressId || qty <= 0) return;
    setAssignBusy(true);
    setAssignError(null);
    try {
      await assignContainersToAddress({
        businessId,
        customerId,
        stockItemId: assignItem,
        addressId: assignAddressId,
        quantity: qty,
      });
      setAssignQty('');
      await load();
    } catch (err: any) {
      setAssignError(err?.message || 'Failed to move containers');
    } finally {
      setAssignBusy(false);
    }
  };

  const balanceGroups = (() => {
    const groups = new Map<string, { label: string; rows: BalanceRow[] }>();
    for (const b of heldItems) {
      const key = b.customer_address_id || (b.address_label === 'Account' ? 'account' : 'unassigned');
      const group = groups.get(key) || { label: b.address_label, rows: [] };
      group.rows.push(b);
      groups.set(key, group);
    }
    return Array.from(groups.entries());
  })();

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Package className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Containers</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAdjust}
            className="inline-flex items-center justify-center px-3.5 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-all duration-150 active:scale-[0.98]"
          >
            <SlidersHorizontal className="h-4 w-4 mr-1.5" />
            Adjust
          </button>
          {heldItems.length > 0 && (
            <button
              onClick={() => {
                setShowPickup(v => !v);
                setShowAdjust(false);
                setPickupItem(heldItems[0]?.stock_item_id || '');
                setPickupAddressId(heldItems[0]?.customer_address_id || '');
                setPickupError(null);
                setPickupSuccess(null);
              }}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all duration-150 active:scale-[0.98]"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Record pickup
            </button>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Balances */}
        {heldItems.length > 0 ? (
          <>
            <div className="space-y-3">
              {balanceGroups.map(([key, group]) => (
                <div key={key}>
                  {balanceGroups.length > 1 || key !== 'account' ? (
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{group.label}</p>
                  ) : null}
                  <div className="space-y-2">
                    {group.rows.map(b => (
                      <div key={`${key}-${b.stock_item_id}`} className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">
                          {b.quantity} × {b.item_name}
                        </span>
                        <span className="text-sm text-gray-600">{fmtMoney(b.deposit_cents_held)} deposit held</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {unassignedHeld.length > 0 && addresses.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-700">Move unassigned containers to an address</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <select
                    value={assignItem}
                    onChange={e => setAssignItem(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-xl bg-white"
                  >
                    {unassignedHeld.map(b => (
                      <option key={b.stock_item_id} value={b.stock_item_id}>
                        {b.item_name} ({b.quantity})
                      </option>
                    ))}
                  </select>
                  <select
                    value={assignAddressId}
                    onChange={e => setAssignAddressId(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-xl bg-white"
                  >
                    <option value="">Choose address</option>
                    {addresses.map(a => (
                      <option key={a.id} value={a.id}>{a.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={assignQty}
                    onChange={e => setAssignQty(e.target.value)}
                    placeholder="Qty"
                    className="px-3 py-2 text-sm border border-gray-300 rounded-xl"
                  />
                </div>
                {assignError && <p className="text-xs text-red-600">{assignError}</p>}
                <button
                  type="button"
                  onClick={handleAssign}
                  disabled={assignBusy || !assignItem || !assignAddressId || (parseInt(assignQty) || 0) <= 0}
                  className="px-3 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-xl inline-flex items-center gap-1.5"
                >
                  {assignBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Move
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500">
              Total deposits held: <span className="font-semibold text-gray-700">{fmtMoney(totalHeldCents)}</span> — refunded when containers come back.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500">No containers on hand — all deposits settled.</p>
        )}

        {/* Manual adjustment form */}
        {showAdjust && (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Adjust containers on hand</h4>
            <p className="text-xs text-gray-500">
              Manual correction of the count — miscounts, pre-existing jugs, damaged write-offs.
              Every adjustment is logged with your name and reason. Deposits are not charged or
              refunded unless you tick the deposit option.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Container</label>
              <select
                value={adjustItem}
                onChange={e => setAdjustItem(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {adjustableItems.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.name} — {onHandFor(i.id)} on hand
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                <div className="flex gap-1.5">
                  {([
                    { id: 'add', label: '+ Add' },
                    { id: 'remove', label: '− Remove' },
                  ] as Array<{ id: 'add' | 'remove'; label: string }>).map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setAdjustDir(opt.id)}
                      className={`flex-1 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-colors ${
                        adjustDir === opt.id
                          ? opt.id === 'add'
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  value={adjustQty}
                  onChange={e => setAdjustQty(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={adjustReason}
                onChange={e => setAdjustReason(e.target.value)}
                placeholder="e.g. had 3 jugs before we started tracking, recount at delivery"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={adjustTrackDeposit}
                onChange={e => setAdjustTrackDeposit(e.target.checked)}
                className="accent-blue-600 mt-0.5"
              />
              <span className="text-xs text-gray-600">
                Count deposit value too — use when a deposit was actually collected (or refunded)
                for these containers outside the system.
              </span>
            </label>
            {adjustError && <p className="text-xs text-red-600">{adjustError}</p>}
            {adjustSuccess && <p className="text-xs text-emerald-600">{adjustSuccess}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => setShowAdjust(false)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleAdjust}
                disabled={adjustBusy || !adjustItem || (parseInt(adjustQty) || 0) <= 0 || !adjustReason.trim()}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-xl transition-colors inline-flex items-center justify-center gap-2"
              >
                {adjustBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                Save adjustment
              </button>
            </div>
          </div>
        )}

        {/* Pickup / refund form */}
        {showPickup && heldItems.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Record pickup / refund</h4>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Container</label>
              <select
                value={pickupItem}
                onChange={e => setPickupItem(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from(new Map(heldItems.map(b => [b.stock_item_id, b])).values()).map(b => (
                  <option key={b.stock_item_id} value={b.stock_item_id}>
                    {b.item_name} — {onHandFor(b.stock_item_id)} on hand
                  </option>
                ))}
              </select>
            </div>
            {addresses.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                <select
                  value={pickupAddressId}
                  onChange={e => setPickupAddressId(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Not tied to an address</option>
                  {addresses.map(a => (
                    <option key={a.id} value={a.id}>{a.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  A location uses that address first, then containers that aren’t tied to a site. It never refunds a deposit from a different address.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Returned (refund deposit)</label>
                <input
                  type="number"
                  min="0"
                  value={pickupReturned}
                  onChange={e => setPickupReturned(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Lost (deposit kept)</label>
                <input
                  type="number"
                  min="0"
                  value={pickupLost}
                  onChange={e => setPickupLost(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            {/* Exact payout preview from the deposit lots (rates actually paid) */}
            {pickupQuote && (Math.max(0, parseInt(pickupReturned) || 0) > 0) && (
              <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-3 py-2.5">
                <p className="text-xs font-semibold text-blue-900">
                  Refund due: {fmtMoney(pickupQuote.refund_cents)}
                </p>
                {pickupQuote.allocation.filter(l => l.rate_cents > 0).length > 0 && (
                  <p className="text-[11px] text-blue-800 mt-0.5">
                    {pickupQuote.allocation
                      .filter(l => l.rate_cents > 0)
                      .map(l => `${l.quantity} × ${fmtMoney(l.rate_cents)}`)
                      .join(' + ')}
                    {' '}— at the deposit rates this customer originally paid.
                  </p>
                )}
                {pickupQuote.unmatched_quantity > 0 && (
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    {`${pickupQuote.unmatched_quantity} of these ${pickupQuote.unmatched_quantity === 1 ? 'isn\u2019t' : 'aren\u2019t'} on record — refunded at today\u2019s ${fmtMoney(pickupQuote.unmatched_rate_cents ?? 0)} rate and the count is corrected.`}
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Refund to</label>
              <RefundDestinationPicker
                businessId={businessId}
                customerId={customerId}
                amountCents={pickupQuote?.refund_cents ?? 0}
                value={pickupRefund}
                onChange={next => setPickupRefund(next as RefundDestination)}
                destinations={['wallet', 'card', 'cash', 'e_transfer']}
                hints={{ wallet: 'auto-applies to their next invoice' }}
                reference={pickupReference}
                onReferenceChange={setPickupReference}
                tone="blue"
              />
            </div>
            <input
              type="text"
              value={pickupNotes}
              onChange={e => setPickupNotes(e.target.value)}
              placeholder="Notes (optional) — e.g. final pickup, service ended"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {pickupError && <p className="text-xs text-red-600">{pickupError}</p>}
            {pickupSuccess && <p className="text-xs text-emerald-600">{pickupSuccess}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => setShowPickup(false)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl transition-colors"
              >
                Close
              </button>
              <button
                onClick={handlePickup}
                disabled={pickupBusy || !pickupItem || (Math.max(0, parseInt(pickupReturned) || 0) + Math.max(0, parseInt(pickupLost) || 0)) === 0}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 rounded-xl transition-colors inline-flex items-center justify-center gap-2"
              >
                {pickupBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                Record
              </button>
            </div>
          </div>
        )}

        {/* Audit trail */}
        {hasActivity && (
          <div>
            <button
              onClick={() => setShowHistory(v => !v)}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900"
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              Audit log ({ledger.length})
            </button>
            {showHistory && (
              <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto">
                {ledger.map(t => {
                  const delta = rowDelta(t);
                  // Refund/forfeit rows carry an exact lot allocation; fall
                  // back to the naive rate estimate for older rows.
                  const allocTotal = Array.isArray(t.allocation)
                    ? t.allocation.reduce((n, a) => n + (a.refund_cents || 0), 0)
                    : null;
                  const moneyCents =
                    (t.type === 'returned' || t.type === 'lost') && allocTotal !== null
                      ? -allocTotal
                      : delta * t.deposit_cents_per_unit;
                  return (
                    <div key={t.id} className="flex items-start justify-between rounded-lg bg-gray-50 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900">
                          {TYPE_LABELS[t.type]}{' '}
                          {t.type === 'adjustment' ? (
                            <span className={t.quantity > 0 ? 'text-emerald-700' : 'text-amber-700'}>
                              {t.quantity > 0 ? `+${t.quantity}` : t.quantity}
                            </span>
                          ) : (
                            t.quantity
                          )}{' '}
                          × {t.item_name}
                          {t.type === 'returned' && t.refund_method && (
                            <span className="text-gray-500 font-normal">
                              {' '}· {t.refund_method === 'wallet' ? 'refunded to wallet' : t.refund_method === 'invoice_credit' ? 'netted on invoice' : t.refund_method === 'card' ? 'refunded to card' : 'refunded externally'}
                            </span>
                          )}
                          {t.type === 'lost' && <span className="text-gray-500 font-normal"> · deposit forfeited</span>}
                          {t.type === 'adjustment' && <span className="text-gray-500 font-normal"> · manual adjustment</span>}
                        </p>
                        {t.notes && <p className="text-[11px] text-gray-500 mt-0.5">{t.notes}</p>}
                        {(t.type === 'returned' || t.type === 'lost') && Array.isArray(t.allocation) && t.allocation.length > 0 && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {t.type === 'returned' ? 'Refunded' : 'Forfeited'}:{' '}
                            {t.allocation.map(a => `${a.quantity} × ${fmtMoney(a.rate_cents)}`).join(' + ')}
                            {' '}(rates originally paid)
                          </p>
                        )}
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {new Date(t.created_at).toLocaleString(undefined, {
                            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                          })}
                          {t.staff_name ? ` · by ${t.staff_name}` : ''}
                        </p>
                      </div>
                      <div className="text-right ml-3 flex-shrink-0">
                        <p className={`text-xs font-semibold whitespace-nowrap ${delta > 0 ? 'text-gray-700' : 'text-emerald-600'}`}>
                          {delta > 0 ? '+' : '−'}{Math.abs(delta)}
                        </p>
                        <p className="text-[11px] text-gray-400 whitespace-nowrap">
                          {moneyCents !== 0 ? `${moneyCents > 0 ? '+' : '−'}${fmtMoney(moneyCents)}` : '—'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
